import { Vector3, Mesh, Scene } from '@babylonjs/core';
import { Block } from './Block';

export const CHUNK_SIZE = 16;
export const CHUNK_HEIGHT = 16;

export interface ChunkCoord {
  x: number;
  z: number;
}

export class Chunk {
  public readonly coord: ChunkCoord;
  public readonly worldPosition: Vector3;

  private blocks: Uint8Array;
  private mesh: Mesh | null = null;
  private isDirty: boolean = true;
  private scene: Scene;

  constructor(scene: Scene, coord: ChunkCoord) {
    this.scene = scene;
    this.coord = coord;
    this.worldPosition = new Vector3(
      coord.x * CHUNK_SIZE,
      0,
      coord.z * CHUNK_SIZE
    );

    // Flat array for better performance: y * (SIZE * SIZE) + z * SIZE + x
    this.blocks = new Uint8Array(CHUNK_SIZE * CHUNK_HEIGHT * CHUNK_SIZE);
    this.blocks.fill(Block.AIR);
  }

  private getIndex(x: number, y: number, z: number): number {
    return y * (CHUNK_SIZE * CHUNK_SIZE) + z * CHUNK_SIZE + x;
  }

  public isValidPosition(x: number, y: number, z: number): boolean {
    return (
      x >= 0 && x < CHUNK_SIZE &&
      y >= 0 && y < CHUNK_HEIGHT &&
      z >= 0 && z < CHUNK_SIZE
    );
  }

  public getBlock(x: number, y: number, z: number): number {
    if (!this.isValidPosition(x, y, z)) {
      return Block.AIR;
    }
    return this.blocks[this.getIndex(x, y, z)];
  }

  public setBlock(x: number, y: number, z: number, blockId: number): void {
    if (!this.isValidPosition(x, y, z)) {
      return;
    }
    const index = this.getIndex(x, y, z);
    if (this.blocks[index] !== blockId) {
      this.blocks[index] = blockId;
      this.isDirty = true;
    }
  }

  public getBlockWorld(worldX: number, worldY: number, worldZ: number): number {
    const localX = worldX - this.worldPosition.x;
    const localY = worldY;
    const localZ = worldZ - this.worldPosition.z;
    return this.getBlock(localX, localY, localZ);
  }

  public setBlockWorld(worldX: number, worldY: number, worldZ: number, blockId: number): void {
    const localX = worldX - this.worldPosition.x;
    const localY = worldY;
    const localZ = worldZ - this.worldPosition.z;
    this.setBlock(localX, localY, localZ, blockId);
  }

  public fill(blockId: number, fromY: number = 0, toY: number = CHUNK_HEIGHT): void {
    for (let y = fromY; y < toY; y++) {
      for (let z = 0; z < CHUNK_SIZE; z++) {
        for (let x = 0; x < CHUNK_SIZE; x++) {
          this.setBlock(x, y, z, blockId);
        }
      }
    }
  }

  public fillLayer(y: number, blockId: number): void {
    for (let z = 0; z < CHUNK_SIZE; z++) {
      for (let x = 0; x < CHUNK_SIZE; x++) {
        this.setBlock(x, y, z, blockId);
      }
    }
  }

  public setColumn(x: number, z: number, height: number, blockId: number): void {
    for (let y = 0; y < height; y++) {
      this.setBlock(x, y, z, blockId);
    }
  }

  public needsRebuild(): boolean {
    return this.isDirty;
  }

  public markDirty(): void {
    this.isDirty = true;
  }

  public markClean(): void {
    this.isDirty = false;
  }

  public setMesh(mesh: Mesh | null): void {
    if (this.mesh) {
      this.mesh.dispose();
    }
    this.mesh = mesh;
  }

  public getMesh(): Mesh | null {
    return this.mesh;
  }

  public getScene(): Scene {
    return this.scene;
  }

  public getBlocks(): Uint8Array {
    return this.blocks;
  }

  public isEmpty(): boolean {
    for (let i = 0; i < this.blocks.length; i++) {
      if (this.blocks[i] !== Block.AIR) {
        return false;
      }
    }
    return true;
  }

  public getBlockCount(): number {
    let count = 0;
    for (let i = 0; i < this.blocks.length; i++) {
      if (this.blocks[i] !== Block.AIR) {
        count++;
      }
    }
    return count;
  }

  public dispose(): void {
    if (this.mesh) {
      this.mesh.dispose();
      this.mesh = null;
    }
  }

  public static coordToKey(coord: ChunkCoord): string {
    return `${coord.x},${coord.z}`;
  }

  public static keyToCoord(key: string): ChunkCoord {
    const [x, z] = key.split(',').map(Number);
    return { x, z };
  }

  public static worldToChunkCoord(worldX: number, worldZ: number): ChunkCoord {
    return {
      x: Math.floor(worldX / CHUNK_SIZE),
      z: Math.floor(worldZ / CHUNK_SIZE),
    };
  }

  public static worldToLocalCoord(worldX: number, worldY: number, worldZ: number): Vector3 {
    return new Vector3(
      ((worldX % CHUNK_SIZE) + CHUNK_SIZE) % CHUNK_SIZE,
      worldY,
      ((worldZ % CHUNK_SIZE) + CHUNK_SIZE) % CHUNK_SIZE
    );
  }
}
